/**
 * InfraHostsListPage (#670)
 *
 * Platform-admin-gated fleet view of edge hosts. Polls every 30s.
 * Layout: page header + 4-tile fleet summary + filter shelf + DataTable.
 */

import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import {
  Button, Input, Spinner, Alert, Skeleton,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  DataTable, DataTableTable, DataTableHeader, DataTableBody,
  DataTableRow, DataTableHead, DataTableCell, DataTableEmpty,
  EmptyState,
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
} from '@agion/ui'
import { Server, Search, AlertTriangle, Clock, RefreshCw, ServerOff } from 'lucide-react'
import { PageHeader } from '@/layouts/InfraPageHeader'
import { listHosts, MonitoringUnavailableError, retryMonitoringRequest } from './infra_monitoring.service'
import type { MonitoringHost, HostStatus, AlertSeverity } from '@/core/infra_types'
import { formatRelativeTime } from '@/core/infra_utils'
import { SummaryTile, HostStatusBadge, FilterShelf } from './MonitoringPrimitives'
import { SEVERITY_TONE } from './monitoring_tokens'

type SortKey = 'name' | 'environment' | 'status' | 'heartbeat'
type SortOrder = 'asc' | 'desc'

const STATUS_RANK: Record<HostStatus, number> = { offline: 0, degraded: 1, online: 2 }
const SEVERITY_RANK: Record<AlertSeverity, number> = { critical: 0, warning: 1, info: 2 }

export function InfraHostsListPage() {
  const { t } = useTranslation(['pages'])
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [envFilter, setEnvFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState<SortKey>('status')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')

  const { data: hosts = [], isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['monitoring', 'hosts'],
    queryFn: listHosts,
    staleTime: 30000,
    refetchInterval: (query) => query.state.error ? false : 30000,
    retry: retryMonitoringRequest,
  })

  const environments = useMemo(() => {
    const envSet = new Set(hosts.map((h) => h.environmentName))
    return Array.from(envSet).sort()
  }, [hosts])

  const summary = useMemo(() => {
    const total = hosts.length
    let online = 0, degraded = 0, offline = 0
    for (const h of hosts) {
      if (h.status === 'online') online++
      else if (h.status === 'degraded') degraded++
      else offline++
    }
    return { total, online, degraded, offline }
  }, [hosts])

  const filtered = useMemo(() => {
    const out = hosts.filter((host) => {
      const q = searchQuery.toLowerCase()
      const matchesSearch =
        host.name.toLowerCase().includes(q) || host.environmentName.toLowerCase().includes(q)
      const matchesStatus = statusFilter === 'all' || host.status === statusFilter
      const matchesEnv = envFilter === 'all' || host.environmentName === envFilter
      return matchesSearch && matchesStatus && matchesEnv
    })
    out.sort((a, b) => {
      const dir = sortOrder === 'asc' ? 1 : -1
      switch (sortBy) {
        case 'name':        return a.name.localeCompare(b.name) * dir
        case 'environment': return a.environmentName.localeCompare(b.environmentName) * dir
        case 'status':      return (STATUS_RANK[a.status] - STATUS_RANK[b.status]) * dir
        case 'heartbeat': {
          const ta = new Date(a.lastHeartbeat).getTime()
          const tb = new Date(b.lastHeartbeat).getTime()
          return (ta - tb) * dir
        }
      }
    })
    return out
  }, [hosts, searchQuery, statusFilter, envFilter, sortBy, sortOrder])

  const isUnavailable = error instanceof MonitoringUnavailableError
  const filtersActive = searchQuery !== '' || statusFilter !== 'all' || envFilter !== 'all'
  const showRealError = error && !isUnavailable

  function handleSort(key: SortKey) {
    if (sortBy === key) {
      setSortOrder((p) => (p === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(key)
      setSortOrder('asc')
    }
  }

  return (
    <div className="container-standard">
      <PageHeader
        icon={Server}
        iconColor="text-blue-600"
        title={t('pages:monitoring.hosts.title', 'Edge Hosts')}
        description={t('pages:monitoring.hosts.description', 'Monitor edge host health and alerts across all environments')}
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label={t('pages:monitoring.hosts.refresh', 'Refresh hosts')}
            className="flex items-center gap-2"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            {t('pages:monitoring.hosts.refresh', 'Refresh')}
          </Button>
        }
      />

      {isUnavailable && (
        <Alert variant="info" className="mb-6">
          {t('pages:monitoring.hosts.unavailable', 'Host data could not be loaded. Refresh to try again.')}
        </Alert>
      )}

      {/* Fleet summary tiles */}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryTile
          label={t('pages:monitoring.hosts.summary.total', 'Total Hosts')}
          value={isLoading ? <Skeleton className="h-8 w-12" /> : error ? '-' : summary.total}
          icon={Server}
          tone="neutral"
          caption={t('pages:monitoring.hosts.summary.totalCaption', 'across all environments')}
        />
        <SummaryTile
          label={t('pages:monitoring.hosts.summary.online', 'Online hosts')}
          value={isLoading ? <Skeleton className="h-8 w-12" /> : error ? '-' : summary.online}
          icon={Server}
          tone="success"
        />
        <SummaryTile
          label={t('pages:monitoring.hosts.summary.degraded', 'Degraded hosts')}
          value={isLoading ? <Skeleton className="h-8 w-12" /> : error ? '-' : summary.degraded}
          icon={AlertTriangle}
          tone="warning"
        />
        <SummaryTile
          label={t('pages:monitoring.hosts.summary.offline', 'Offline hosts')}
          value={isLoading ? <Skeleton className="h-8 w-12" /> : error ? '-' : summary.offline}
          icon={ServerOff}
          tone="critical"
        />
      </div>

      <FilterShelf>
        <div className="relative min-w-0 flex-1">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input
            placeholder={t('pages:monitoring.hosts.search', 'Search hosts...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" aria-label={t('pages:monitoring.hosts.filters.status', 'Filter by status')}>
            <SelectValue placeholder={t('pages:monitoring.hosts.filters.allStatuses', 'All Statuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages:monitoring.hosts.filters.allStatuses', 'All Statuses')}</SelectItem>
            <SelectItem value="online">{t('pages:monitoring.hosts.status.online', 'Online')}</SelectItem>
            <SelectItem value="degraded">{t('pages:monitoring.hosts.status.degraded', 'Degraded')}</SelectItem>
            <SelectItem value="offline">{t('pages:monitoring.hosts.status.offline', 'Offline')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={envFilter} onValueChange={setEnvFilter}>
          <SelectTrigger className="w-52" aria-label={t('pages:monitoring.hosts.filters.environment', 'Filter by environment')}>
            <SelectValue placeholder={t('pages:monitoring.hosts.filters.allEnvironments', 'All Environments')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages:monitoring.hosts.filters.allEnvironments', 'All Environments')}</SelectItem>
            {environments.map((env) => (
              <SelectItem key={env} value={env}>{env}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterShelf>

      {/* Body */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : showRealError ? (
        <Alert variant="error">
          {error instanceof Error ? error.message : t('pages:monitoring.hosts.error', 'Failed to load hosts')}
        </Alert>
      ) : isUnavailable && filtered.length === 0 ? (
        <DataTable variant="bordered">
          <DataTableTable>
            <DataTableBody>
              <tr>
                <td>
                  <EmptyState
                    icon={ServerOff}
                    title={t('pages:monitoring.hosts.awaitingBackendTitle', 'Unable to load hosts')}
                    description={t('pages:monitoring.hosts.awaitingBackend', 'Host health is currently unknown. Refresh to try again.')}
                  />
                </td>
              </tr>
            </DataTableBody>
          </DataTableTable>
        </DataTable>
      ) : filtered.length === 0 ? (
        <DataTable variant="bordered">
          <DataTableTable>
            <DataTableBody>
              <tr>
                <td>
                  <EmptyState
                    icon={Server}
                    title={
                      filtersActive
                        ? t('pages:monitoring.hosts.noResultsTitle', 'No matching hosts')
                        : t('pages:monitoring.hosts.emptyTitle', 'No hosts registered')
                    }
                    description={
                      filtersActive
                        ? t('pages:monitoring.hosts.noResults', 'No hosts match your filters.')
                        : t('pages:monitoring.hosts.empty', 'No hosts registered yet.')
                    }
                    action={
                      filtersActive ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSearchQuery('')
                            setStatusFilter('all')
                            setEnvFilter('all')
                          }}
                        >
                          {t('pages:monitoring.hosts.clearFilters', 'Clear filters')}
                        </Button>
                      ) : undefined
                    }
                  />
                </td>
              </tr>
            </DataTableBody>
          </DataTableTable>
        </DataTable>
      ) : (
        <DataTable variant="bordered">
          <DataTableTable>
            <DataTableHeader>
              <tr>
                <DataTableHead
                  sortable
                  sorted={sortBy === 'name' ? sortOrder : false}
                  onClick={() => handleSort('name')}
                >
                  {t('pages:monitoring.hosts.columns.name', 'Host')}
                </DataTableHead>
                <DataTableHead
                  sortable
                  sorted={sortBy === 'environment' ? sortOrder : false}
                  onClick={() => handleSort('environment')}
                  className="hidden md:table-cell"
                >
                  {t('pages:monitoring.hosts.columns.environment', 'Environment')}
                </DataTableHead>
                <DataTableHead
                  sortable
                  sorted={sortBy === 'status' ? sortOrder : false}
                  onClick={() => handleSort('status')}
                >
                  {t('pages:monitoring.hosts.columns.status', 'Status')}
                </DataTableHead>
                <DataTableHead
                  sortable
                  sorted={sortBy === 'heartbeat' ? sortOrder : false}
                  onClick={() => handleSort('heartbeat')}
                  className="hidden md:table-cell"
                >
                  {t('pages:monitoring.hosts.columns.lastHeartbeat', 'Last Heartbeat')}
                </DataTableHead>
                <DataTableHead>
                  {t('pages:monitoring.hosts.columns.alerts', 'Alerts')}
                </DataTableHead>
              </tr>
            </DataTableHeader>
            <DataTableBody>
              {filtered.map((host) => (
                <DataTableRow
                  key={host.id}
                  isClickable
                  onClick={() => navigate(`/hosts/${host.id}`)}
                >
                  <DataTableCell className="font-medium text-slate-900 dark:text-slate-100">
                    {host.name}
                  </DataTableCell>
                  <DataTableCell className="hidden md:table-cell text-slate-500">
                    {host.environmentName}
                  </DataTableCell>
                  <DataTableCell>
                    <HostStatusBadge
                      status={host.status}
                      label={t(`pages:monitoring.hosts.status.${host.status}`, host.status)}
                    />
                  </DataTableCell>
                  <DataTableCell className="hidden md:table-cell">
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-500">
                      <Clock size={14} aria-hidden="true" />
                      {formatRelativeTime(host.lastHeartbeat)}
                    </span>
                  </DataTableCell>
                  <DataTableCell>
                    <AlertsCell alerts={host.firingAlerts} />
                  </DataTableCell>
                </DataTableRow>
              ))}
              {filtered.length === 0 && (
                <DataTableEmpty
                  colSpan={5}
                  message={t('pages:monitoring.hosts.noResults', 'No hosts match your filters.')}
                />
              )}
            </DataTableBody>
          </DataTableTable>
        </DataTable>
      )}
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="divide-y divide-slate-200 dark:divide-slate-700">
        <div className="bg-slate-50 dark:bg-slate-800 px-4 py-3 flex items-center">
          <Spinner size="sm" />
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="px-4 py-4 flex items-center gap-4">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-32 hidden md:block" />
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-4 w-24 hidden md:block" />
            <Skeleton className="h-5 w-28 ml-auto rounded-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

function AlertsCell({ alerts }: { alerts: MonitoringHost['firingAlerts'] }) {
  const { t } = useTranslation(['pages'])

  if (alerts.length === 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-slate-400 dark:text-slate-500">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
        {t('pages:monitoring.hosts.noAlerts', 'No alerts')}
      </span>
    )
  }

  // Sort by severity then take top 3 to display
  const sorted = [...alerts].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity])
  const displayAlerts = sorted.slice(0, 3)
  const remaining = sorted.length - 3

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex flex-wrap items-center gap-1.5">
        {displayAlerts.map((alert) => (
          <Tooltip key={alert.id}>
            <TooltipTrigger asChild>
              <span
                className={
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ' +
                  (SEVERITY_TONE[alert.severity] === 'critical'
                    ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-950/40 dark:text-rose-300'
                    : SEVERITY_TONE[alert.severity] === 'warning'
                    ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                    : 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-300')
                }
              >
                <AlertTriangle size={12} aria-hidden="true" />
                {alert.name}
              </span>
            </TooltipTrigger>
            <TooltipContent>{alert.message ?? alert.name}</TooltipContent>
          </Tooltip>
        ))}
        {remaining > 0 && (
          <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('pages:monitoring.hosts.moreAlerts', '+{{count}}', { count: remaining })}
          </span>
        )}
      </div>
    </TooltipProvider>
  )
}
